import dayjs from "dayjs"

import * as journeysApi from "@/api/journeys/api"
import useInitDateRange from "@/composables/useInitDateRange"
import useRouterProtect from "@/composables/useRouterProtect/index.js"
import DefaultLayout from "@/layouts/DefaultLayout/DefaultLayout.vue"
import createAuthRouteMeta from "@/utils/createAuthRouteMeta"
import PERMISSIONS from "@/utils/permissions/index.js"

export default [
    {
        path: "/automated-marketing",
        component: DefaultLayout,
        redirect: "/automated-marketing/journeys",
        name: "automated-marketing",
        meta: {
            isShow: true,
            name: "智能營運",
            index: "3",
            position: "left",
            menuGroup: "automated-marketing",
            permissions: [PERMISSIONS.VIEW_JOURNEY, PERMISSIONS.VIEW_ARCHIVED_JOURNEY, PERMISSIONS.VIEW_JOURNEY_ORDER],
        },
        children: [
            {
                path: "journeys", // 智能劇本列表
                component: () => import("@/pages/AutomatedMarketingGroup/AutomatedMarketingJourneysPage.vue"),
                name: "journeys-list",
                meta: {
                    requiresAuth: true,
                    permissions: [PERMISSIONS.VIEW_JOURNEY],
                    isSubShow: true,
                    subIndex: "1",
                    subGroup: "journey",
                    subGroupName: "智能劇本列表",
                    alternativeGroup: "automated-marketing",
                },
                props: { isArchived: false },
            },
            {
                path: "journeys/:id", // 智能劇本細節頁
                component: () => import("@/pages/AutomatedMarketingGroup/AutomatedMarketingJourneysDetailPage.vue"),
                name: "journeys-detail",
                meta: createAuthRouteMeta({
                    permissions: [PERMISSIONS.VIEW_JOURNEY_DETAIL],
                    isSubShow: false,
                    subGroup: "journey",
                }),
                props: true,
                children: [
                    {
                        path: "", // 智能劇本細節 - 內容
                        component: () =>
                            import("@/pages/AutomatedMarketingGroup/AutomatedMarketingJourneysDetailContentPage.vue"),
                        name: "journeys-detail-content",

                        props: true,
                        beforeEnter: async (to) => {
                            const [path, response] = await useRouterProtect({
                                to,
                                PERMISSIONS: PERMISSIONS.VIEW_JOURNEY_DETAIL,
                                api: journeysApi.getJourney,
                            })

                            if (path === true) {
                                to.params.initialJourneyResponse = response
                            }
                            return path
                        },
                    },
                    {
                        path: "setting", // 智能劇本細節 - 設定
                        component: () =>
                            import("@/pages/AutomatedMarketingGroup/AutomatedMarketingJourneysDetailSettingPage.vue"),
                        name: "journeys-detail-setting",

                        props: true,
                        beforeEnter: async (to) => {
                            const [path, response] = await useRouterProtect({
                                to,
                                PERMISSIONS: PERMISSIONS.VIEW_JOURNEY_DETAIL,
                                api: journeysApi.getJourney,
                            })

                            if (path === true) {
                                to.params.initialJourneyResponse = response
                            }
                            return path
                        },
                    },
                    {
                        path: "effective", // 智能劇本 - 成效
                        component: () =>
                            import("@/pages/AutomatedMarketingGroup/AutomatedMarketingJourneysDetailEffectivePage.vue"),
                        name: "journeys-detail-effective",

                        props: true,
                        beforeEnter: async (to) => {
                            const [path, response] = await useRouterProtect({
                                to,
                                PERMISSIONS: PERMISSIONS.VIEW_JOURNEY_DETAIL,
                                api: journeysApi.getJourney,
                            })

                            if (path === true) {
                                to.params.initialJourneyResponse = response
                                const startAt = dayjs(response.data.startAt)
                                const endAt = dayjs(response.data.endAt)
                                const [start, end] = useInitDateRange(startAt, endAt, dayjs(), 0)
                                to.params.searchDates = [start, end]
                            }
                            return path
                        },
                    },
                ],
            },
            {
                path: "journeys/add", // 新增智能劇本
                component: () => import("@/pages/AutomatedMarketingGroup/AutomatedMarketingJourneysAddPage.vue"),
                name: "add-journeys",
                meta: createAuthRouteMeta({
                    permissions: [PERMISSIONS.ADD_JOURNEY],
                    isSubShow: false,
                    subGroup: "journey",
                }),
            },
            {
                path: "journeys/:id/edit", // 編輯智能劇本
                component: () => import("@/pages/AutomatedMarketingGroup/AutomatedMarketingJourneysEditPage.vue"),
                name: "edit-journeys",
                meta: createAuthRouteMeta({
                    permissions: [PERMISSIONS.EDIT_JOURNEY],
                    isSubShow: false,
                    subGroup: "journey",
                }),
                props: true,
                beforeEnter: async (to) => {
                    const [path, response] = await useRouterProtect({
                        to,
                        PERMISSIONS: PERMISSIONS.EDIT_JOURNEY,
                        api: journeysApi.getJourney,
                    })

                    if (path === true) {
                        to.params.initialJourneyResponse = response
                    }
                    return path
                },
                children: [
                    {
                        path: "content", // 智能劇本編輯 - 劇本內容
                        component: () =>
                            import("@/pages/AutomatedMarketingGroup/AutomatedMarketingJourneysEditContentPage.vue"),
                        name: "edit-journeys-content",
                        props: true,
                        beforeEnter: async (to) => {
                            const [path, response] = await useRouterProtect({
                                to,
                                PERMISSIONS: PERMISSIONS.EDIT_JOURNEY,
                                api: journeysApi.getJourney,
                            })

                            if (path === true) {
                                to.params.initialJourneyResponse = response
                            }
                            return path
                        },
                    },
                    {
                        path: "setting", // 智能劇本編輯 - 劇本設定
                        component: () =>
                            import("@/pages/AutomatedMarketingGroup/AutomatedMarketingJourneysEditSettingPage.vue"),
                        name: "edit-journeys-setting",
                        props: true,
                        beforeEnter: async (to) => {
                            const [path, response] = await useRouterProtect({
                                to,
                                PERMISSIONS: PERMISSIONS.EDIT_JOURNEY,
                                api: journeysApi.getJourney,
                            })

                            if (path === true) {
                                to.params.initialJourneyResponse = response
                            }
                            return path
                        },
                    },
                ],
            },
            {
                path: "journeys/:id/review", // 審核智能劇本
                component: () => import("@/pages/AutomatedMarketingGroup/AutomatedMarketingJourneysReviewPage.vue"),
                name: "review-journeys",
                meta: createAuthRouteMeta({
                    permissions: [PERMISSIONS.REVIEW_JOURNEY],
                    isSubShow: false,
                    subGroup: "journey",
                }),
                props: true,
                beforeEnter: async (to) => {
                    const [path, response] = await useRouterProtect({
                        to,
                        PERMISSIONS: PERMISSIONS.REVIEW_JOURNEY,
                        api: journeysApi.getJourney,
                    })

                    if (path === true) {
                        to.params.initialJourneyResponse = response
                    }
                    return path
                },
            },
            {
                path: "journeys/order-set", // 智能劇本排序列表
                component: () => import("@/pages/AutomatedMarketingGroup/AutomatedMarketingJourneysOrderSetPage.vue"),
                name: "journeys-order-set",
                meta: {
                    requiresAuth: true,
                    permissions: [PERMISSIONS.VIEW_JOURNEY_ORDER],
                    isSubShow: true,
                    subIndex: "2",
                    subGroup: "journey-order",
                    subGroupName: "智能劇本排序列表",
                    alternativeGroup: "automated-marketing",
                },
            },
            {
                path: "archived", // 智能劇本封存區
                component: () => import("@/pages/AutomatedMarketingGroup/AutomatedMarketingJourneysPage.vue"),
                name: "journeys-archived",
                meta: {
                    requiresAuth: true,
                    permissions: [PERMISSIONS.VIEW_ARCHIVED_JOURNEY],
                    isSubShow: true,
                    subIndex: "3",
                    subGroup: "journey",
                    subGroupName: "智能劇本封存區",
                    alternativeGroup: "automated-marketing",
                },
                props: { isArchived: true },
            },
        ],
    },
]
